import React, { useEffect, useState } from "react";
import { fetchAllDailyReports } from "../../Redux/Slice/Day";
import { Autocomplete, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import DynamicTable from "../../Components/Table/DynamicTable";
import Loader from "../../Components/Loader/Loader";
import { useNavigate } from "react-router-dom";
import { FaChevronDown, FaTimes } from "react-icons/fa";

/* ================= UTC HELPERS ================= */
const getUTCDate = (dateStr) => new Date(dateStr).getUTCDate();
const getUTCMonth = (dateStr) => new Date(dateStr).getUTCMonth();
const getUTCYear = (dateStr) => new Date(dateStr).getUTCFullYear();

const MemberDailyWork = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { AllDailyReportData } = useSelector((state) => state.Day);

  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Check if date filters are active
  const isDateFilterActive = fromDate || toDate;

  useEffect(() => {
    dispatch(fetchAllDailyReports());
  }, [dispatch]);

  // Get all months & years available
  const monthsAvailableMap = {};
  AllDailyReportData?.data?.forEach((el) => {
    if (!el?.GeneratedDate) return;
    const y = getUTCYear(el.GeneratedDate);
    const m = getUTCMonth(el.GeneratedDate);
    monthsAvailableMap[`${y}-${m}`] = new Date(Date.UTC(y, m, 1));
  });

  const monthsAvailable = Object?.values(monthsAvailableMap)?.sort(
    (a, b) => b - a,
  );

  /* ================= DEFAULT MONTH ================= */
  useEffect(() => {
    if (!selectedMonth && !selectedYear && monthsAvailable.length > 0) {
      const d = monthsAvailable[0];
      setSelectedMonth(d.getUTCMonth());
      setSelectedYear(d.getUTCFullYear());
    }
  }, [monthsAvailable, selectedMonth, selectedYear]);

  /* ================= LOADER ================= */
  if (!AllDailyReportData?.data) {
    return <Loader />;
  }

  /* ================= MEMBER LIST ================= */
  const MemberOptions =
    AllDailyReportData?.data
      ?.filter((el) => el?.Memberid?.Name)
      ?.map((el) => ({
        Name_id: el?.Memberid?._id,
        Name_name: el?.Memberid?.Name,
      })) || [];

  const uniqueMemberOptions = Array?.from(
    new Map(MemberOptions?.map((m) => [m.Name_id, m]))?.values(),
  );

  /* ================= TABLE ================= */
  const columns = [
    { field: "id", headerName: "No", flex: 0.5 },
    { field: "MemberName", headerName: "Member Name", flex: 1 },
    { field: "GeneratedDate", headerName: "Generated Date", flex: 1 },
    { field: "CompanyId", headerName: "Company Name", flex: 1 },
    { field: "CallNo", headerName: "Call No", flex: 1 },
    {
      field: "Description",
      headerName: "Description",
      width: 300,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: "1.3",
            padding: "4px",
          }}
        >
          {params.value || "--"}
        </div>
      ),
    },
    {
      field: "showTask",
      headerName: "Show Full Task",
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <button
          className="px-4 py-2 rounded bg-[#112A47] text-white hover:bg-[#0c2545]"
          onClick={() =>
            navigate(`/InhouseRnd/CustomerrequustDetail/${params.row.WorkId}`)
          }
        >
          Show Task
        </button>
      ),
    },
  ];

  // Date filter function
  const isDateInRange = (dateString, fromDate, toDate) => {
    if (!fromDate && !toDate) return true;

    const date = new Date(dateString);
    date.setHours(0, 0, 0, 0);

    if (fromDate && toDate) {
      const from = new Date(fromDate);
      from.setHours(0, 0, 0, 0);
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);
      return date >= from && date <= to;
    }

    if (fromDate) {
      const from = new Date(fromDate);
      from.setHours(0, 0, 0, 0);
      return date >= from;
    }

    if (toDate) {
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);
      return date <= to;
    }

    return true;
  };

  const rows =
    AllDailyReportData?.data
      ?.filter((el) => {
        // Member filter
        const memberFilter = selectedMember
          ? el?.Memberid?._id === selectedMember?.Name_id
          : true;

        // Date range filter
        const dateFilter = isDateInRange(el.GeneratedDate, fromDate, toDate);

        return memberFilter && dateFilter;
      })
      ?.sort((a, b) => new Date(b?.GeneratedDate) - new Date(a?.GeneratedDate))
      ?.map((el, index) => ({
        id: index + 1,
        ReportId: el?._id,
        WorkId: el?.WorkId?._id,
        MemberName: el?.Memberid?.Name || "--",
        GeneratedDate: `${getUTCDate(el.GeneratedDate)
          .toString()
          .padStart(2, "0")}-${(getUTCMonth(el.GeneratedDate) + 1)
          .toString()
          .padStart(2, "0")}-${getUTCYear(el.GeneratedDate)}`,
        CompanyId: el?.WorkId?.CompanyName?.WhaClientName || "--",
        CallNo: el?.WorkId?.CallNo || "--",
        Description: el?.Description || "--",
      })) || [];

  // Clear date filters
  const clearDateFilters = () => {
    setFromDate("");
    setToDate("");
  };

  // Get today's date in YYYY-MM-DD format for max attribute
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  /* ================= JSX ================= */
  return (
    <div>
      {/* Title */}
      <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
        Daily Report
      </h3>

      {/* Filter Section */}
      <div className="mt-5 bg-white shadow-md rounded-xl p-4">
        <div className="grid grid-cols-12 gap-4 items-end">
          {/* Member Filter */}
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <label className="font-semibold block mb-2">Member Name</label>
            <Autocomplete
              options={uniqueMemberOptions}
              value={selectedMember}
              onChange={(event, newValue) => setSelectedMember(newValue)}
              getOptionLabel={(option) => option?.Name_name || ""}
              isOptionEqualToValue={(option, value) =>
                option?.Name_id === value?.Name_id
              }
              popupIcon={<FaChevronDown className="text-gray-600 text-sm" />}
              clearIcon={<FaTimes className="text-gray-600 text-xs" />}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Member"
                  size="small"
                  className="bg-white rounded-md custom-input"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: <>{params.InputProps.endAdornment}</>,
                  }}
                />
              )}
            />
          </div>

          {/* From Date Filter */}
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <label className="font-semibold block mb-2">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="bg-[#F3F8FB] w-full p-2 border-2 rounded border-black"
              max={toDate || getTodayDate()}
            />
          </div>

          {/* To Date Filter */}
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <label className="font-semibold block mb-2">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="bg-[#F3F8FB] w-full p-2 border-2 rounded border-black"
              min={fromDate}
              max={getTodayDate()}
            />
          </div>

          {/* Clear Date Filters Button - Hidden when no date filter is active */}
          {isDateFilterActive && (
            <div className="col-span-12 md:col-span-6 lg:col-span-2 flex justify-end">
              <button
                onClick={clearDateFilters}
                className="px-4 py-2 rounded bg-[#112A47] text-white hover:bg-[#0c2545]"
              >
                Clear Date Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow-md rounded-lg overflow-x-auto mt-5">
        <DynamicTable columns={columns} rows={rows} searchable={false} />
      </div>
    </div>
  );
};

export default MemberDailyWork;